import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon, 
  Add as AddIcon, 
  Edit as EditIcon,
  Business as ClientIcon,
} from '@mui/icons-material';

import { fetchClients, fetchClientsCount, createClient } from '../api/features/clientData/clientApi';
import { useNavigate } from 'react-router-dom';
import { useClientCode } from '../api/auth/ClientCodeContext';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, PageLayout, FilterBar } from '../components';

const ROWS_PER_PAGE = 10; // API: firstResult=0&maxResult=10&searched=true&code=...

// SAP Styled Components

const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPClientCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  borderRadius: '8px',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: SAP_SHADOWS.md,
    transform: 'translateY(-2px)',
    borderColor: SAP_COLORS.sapGray3,
  },
}));

const SAPStatusBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '6px',
  backgroundColor: SAP_COLORS.sapGreen,
  borderRadius: '0 2px 2px 0',
}));

export default function Clients() {
  const navigate = useNavigate();
  const { clientCode: contextClientCode, syncWithLocalStorage } = useClientCode();
  const clientCode = contextClientCode || 'admin'; // Fallback to 'admin' if context is undefined
  const isAdmin = clientCode === 'admin';

  // State
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [searchCode, setSearchCode] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  // Applied filters: only these are used for API calls (set when Search is clicked, like Job Details)
  const [appliedSearchCode, setAppliedSearchCode] = useState('');
  const [appliedSelectedClient, setAppliedSelectedClient] = useState<any>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newClientCode, setNewClientCode] = useState('');
  const [newClientName, setNewClientName] = useState('');

  // Debug logging
  logger.log('Clients component - contextClientCode:', contextClientCode, 'clientCode:', clientCode, 'isAdmin:', isAdmin);

  // Load dropdown options
  const loadDropdownOptions = useCallback(async () => {
    logger.log('Loading dropdown options...');
    logger.log('Current clientCode:', clientCode);
    
    try {
      // Ensure we have a valid clientCode
      const effectiveClientCode = clientCode || 'admin';
      if (!clientCode) {
        logger.warn('No clientCode available, using "admin" as fallback');
      }
      
      logger.log('About to call fetchClients with:', { 
        clientCode: effectiveClientCode, 
        firstResult: -1, 
        maxResult: 0 
      });
      
      const clients = await fetchClients({ 
        clientCode: effectiveClientCode, 
        firstResult: -1, 
        maxResult: 0 
      });
      
      logger.log('Client options fetched:', clients);
      const options = clients.map((c: any) => ({ 
        // Display only the client name in the combobox
        label: c.name || c.code || 'Unknown',
        value: c.id, 
        code: c.code || '',
        name: c.name || '',
      }));
      logger.log('Mapped client options:', options);
      setClientOptions(options);
    } catch (error) {
      logger.error('Error loading dropdown options:', error);
      setClientOptions([]);
    }
  }, [clientCode]);

  // Sync with localStorage when component mounts
  useEffect(() => {
    logger.log('Clients component: Syncing with localStorage...');
    syncWithLocalStorage();
  }, [syncWithLocalStorage]);

  // Fetch client options for combobox - removed isAdmin restriction
  useEffect(() => {
    logger.log('useEffect triggered for client options, clientCode:', clientCode);
    logger.log('loadDropdownOptions function:', loadDropdownOptions);
    loadDropdownOptions();
  }, [clientCode, loadDropdownOptions]); // Include loadDropdownOptions in dependencies

  const fetchData = useCallback(async () => {
    const searchParams: any = { searched: true };
    if (appliedSearchCode && appliedSearchCode.trim()) searchParams.code = appliedSearchCode.trim();
    if (appliedSelectedClient && appliedSelectedClient.code) searchParams.code = appliedSelectedClient.code;
    const [clientsData, countData] = await Promise.all([
      fetchClients({
        clientCode,
        firstResult: page * ROWS_PER_PAGE,
        maxResult: ROWS_PER_PAGE,
        ...searchParams,
      }),
      fetchClientsCount(clientCode, searchParams),
    ]);
    setClients(clientsData);
    setRowCount(countData);
  }, [clientCode, page, appliedSearchCode, appliedSelectedClient]);

  // Fetch data only when Search was clicked (hasSearched) and when page or applied filters change (same as Job Details)
  useEffect(() => {
    if (!hasSearched) return;
    setLoading(true);
    fetchData()
      .catch((err) => {
        logger.error('Error fetching clients:', err);
        toast.error('Failed to fetch clients');
        setClients([]);
        setRowCount(0);
      })
      .finally(() => setLoading(false));
  }, [hasSearched, fetchData]);

  const handleSearch = () => {
    setAppliedSearchCode(searchCode);
    setAppliedSelectedClient(selectedClient);
    setHasSearched(true);
    setPage(0);
  };

  const handleReset = () => {
    setSearchCode('');
    setSelectedClient(null);
    setAppliedSearchCode('');
    setAppliedSelectedClient(null);
    setPage(0);
    setHasSearched(false);
    setClients([]);
    setRowCount(0);
  };

  const handleCreateClient = async () => {
    if (!newClientCode.trim() || !newClientName.trim()) {
      toast.error('Please fill in both code and name');
      return;
    }

    try {
      await createClient(clientCode, {
        code: newClientCode.trim(),
        name: newClientName.trim(),
      });
      setCreateDialogOpen(false);
      setNewClientCode('');
      setNewClientName('');
      setHasSearched(true);
      fetchData();
      toast.success('Client created successfully');
    } catch (err) {
      logger.error('Error creating client:', err);
      toast.error('Failed to create client');
    }
  };

  const handleCardClick = (client: any) => {
    navigate(`/clientSettings/${client.id}`, { 
      state: { clientName: client.name, clientId: client.id }
    });
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };



  return (
    <PageLayout
      title="Client Management"
      description="Search and manage client configurations and settings across the system"
      titleSx={{ color: SAP_COLORS.sapBlue }}
    >
      <FilterBar loading={loading}>
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 0' },
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { md: 'none' },
          }}
        >
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
            value={selectedClient}
            onChange={(_, v) => setSelectedClient(v)}
            renderInput={(params) => <TextField {...params} label="Client" fullWidth />}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 0' },
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { md: 'none' },
          }}
        >
          <TextField
            label="Client Code"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            placeholder="Enter client code (partial match)"
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            ml: { md: 2 },
          }}
        >
          <SAPIconButton icon="search" variant="primary" onClick={handleSearch} disabled={loading} aria-label="Search" />
          <SAPIconButton icon="reset" variant="secondary" onClick={handleReset} disabled={loading} aria-label="Reset" />
          <SAPIconButton icon="add" variant="success" onClick={() => setCreateDialogOpen(true)} tooltip="Create New Client" aria-label="Create Client" />
        </Box>
      </FilterBar>

      {/* Data Display */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <CircularProgress 
                size={40} 
                sx={{ color: SAP_COLORS.sapBlue }} 
              />
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  marginBottom: SAP_SPACING.md,
                }}
              >
                No data to display
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Use the filters above and click Search to load clients
              </Typography>
            </Box>
          ) : clients.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  marginBottom: SAP_SPACING.md,
                }}
              >
                No clients found
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Try adjusting your search criteria or click Search again
              </Typography>
            </Box>
          ) : (
            <>
              {/* Client Cards */}
              <Box sx={{ p: 3 }}>
                {/* Status Bar - Show count and pagination info */}
                

                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { 
                    xs: '1fr', 
                    sm: 'repeat(2, 1fr)', 
                    md: 'repeat(3, 1fr)', 
                    lg: 'repeat(4, 1fr)' 
                  },
                  gap: 3
                }}>
                  {clients.map((client) => (
                    <SAPClientCard 
                      key={client.id}
                    >
                      {/* Status Bar */}
                      <SAPStatusBar />
                      
                      <CardContent sx={{ p: 3, pl: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: SAP_COLORS.sapGray7, 
                              fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, 
                              flex: 1 
                            }}
                          >
                            {client.code}
                          </Typography>
                          <IconButton 
                            size="small"
                            onClick={() => handleCardClick(client)}
                            sx={{ 
                              color: SAP_COLORS.sapBlue, 
                              backgroundColor: SAP_COLORS.sapGray1,
                              '&:hover': { 
                                backgroundColor: SAP_COLORS.sapLightBlue 
                              } 
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: SAP_COLORS.sapGray5, 
                            lineHeight: 1.4, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}
                        >
                          {client.name}
                        </Typography>
                      </CardContent>
                    </SAPClientCard>
                  ))}
                </Box>
              </Box>

              {/* Pagination */}
              {clients.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + clients.length, rowCount)} of {rowCount} clients
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handlePageChange(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Client Dialog - aligned with Create User modal spacing */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.lg,
            maxHeight: '90vh',
            m: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            flexShrink: 0,
            pt: 2,
            pb: 2.5,
            px: { xs: 2, sm: 3 },
            bgcolor: SAP_COLORS.sapWhite,
            color: SAP_COLORS.sapGray7,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          Create New Client
        </DialogTitle>

        {/* Main content */}
        {/* Important: add clear gap below header (MUI zeros padding when following DialogTitle) */}
        <DialogContent
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            bgcolor: SAP_COLORS.sapWhite,
            px: { xs: 2, sm: 3 },
            py: 4,
            // Force extra space so first input doesn't touch the header
            paddingTop: '32px !important',
            paddingBottom: '32px !important',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 2, sm: 2.5 },
              minWidth: 0,
            }}
          >
            <TextField
              label="Client Code"
              value={newClientCode}
              onChange={(e) => setNewClientCode(e.target.value)}
              placeholder="Enter client code"
              fullWidth
            />
            <TextField
              label="Client Name"
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              placeholder="Enter client name"
              fullWidth
            />
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2.5,
            gap: 1.5,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
            bgcolor: SAP_COLORS.sapWhite,
          }}
        >
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              color: SAP_COLORS.sapGray5,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateClient}
            variant="contained"
            sx={{
              bgcolor: SAP_COLORS.sapGreen,
              '&:hover': { bgcolor: SAP_COLORS.sapDarkBlue },
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
} 